import React, { Component, Fragment } from 'react';
import PromiseFileReader from 'promise-file-reader';
import PropTypes from 'prop-types';
import Cropper from 'react-cropper';
import styled from 'styled-components';

import { USE_FILE_PICKER } from '../config';

import Modal from '../components/Modal/modal';
import Loading from '../components/loading';

import {
  BackArrow,
  Back,
  SmartViewNavbar,
  SmartButton,
  Arrow,
  NavItem,
  FULL_VH
} from '../components/Common';

import iOSArrowBack from '../icons/ios-arrow-back.svg';
import androidArrowBack from '../icons/android-arrow-back.svg';
import androidCancel from '../icons/android-clear.svg';
import theme, { isiOS } from '../themes';

import Section, { SectionChild } from '../components/section';
import TextImageRow from '../components/text-image-row';
import ManagedComponent from '../components/managed-component';

import 'cropperjs/dist/cropper.css';

class ImageInput extends Component {
  static propTypes = {
    style: PropTypes.object,
    children: PropTypes.node.isRequired,
    onChange: PropTypes.func.isRequired
  }

  handleFile = (evt) => {
    this.props.onChange(evt.target.files[0]);
    this.inputRef.value = null;
  }

  render() {
    return (
      <div style={this.props.style}>
        <input
          type='file'
          accept='image/*'
          style={{display: 'none'}}
          onChange={this.handleFile}
          ref={ref => this.inputRef = ref}
        />
        {React.cloneElement(this.props.children, {
          onClick: () => this.inputRef.click()
        })}
      </div>
    )
  }
}

const ImagePickerButton = ({ onChange, useFilePicker = false, children, ...props }) => {
  const button = (
    <SmartButton
      style={{alignItems: 'center'}}
      {...props}
    >
      {children}
    </SmartButton>
  );

  return useFilePicker ? (
    <ImageInput
      onChange={onChange}
    >
      {button}
    </ImageInput>
  ) : button;
};

const ImagePickerRow = TextImageRow;

const ImageDisplay = styled.img`
  max-width: 100%;
`;

const ImagePickerArrow = styled(Arrow)`
  height: inherit;
`;

const CancelButton = styled.div`
  margin-left: 15px;
`;

const CancelIcon = styled(BackArrow).attrs({
  dangerouslySetInnerHTML: {
    __html: androidCancel
  }
})`
  height: 28px;
`;

const DoneButton = styled.div`
  margin-right: ${isiOS() ? '15px' : '24px'};
`;

const Error = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  height: ${FULL_VH - theme.NavbarHeight}px;
  width: 100vw;
  color: red;
  font-size: 1.15em;
`;

class ImageCropper extends Component {
  static defaultProps = {
    onCancel: () => {}
  };

  state = {
    loading: true,
    ready: false,
    showError: false
  };

  handleCropFinished = () => {
    this.setState({ loading: true, ready: false }, () => {
      setTimeout(() => {
        const {
          active,
          pickedImage,
          imageHeight,
          imageWidth,
          onImagePicked
        } = this.props;

        if (!active || !pickedImage || !this.cropperRef) {
          return;
        }

        const canvas = this.cropperRef.getCroppedCanvas({
          height: imageHeight,
          width: imageWidth
        });

        this.setState({ loading: false });

        onImagePicked({
          // Outputs image/png by default
          // https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toDataURL
          imageUri: canvas.toDataURL(),
          imageSize: {
            width: imageWidth,
            height: imageHeight
          }
        });
      }, 0);
    });
  }

  handleCancelCrop = () => this.props.onCancel()

  handleError = () => this.setState({
    loading: false,
    ready: false,
    showError: true
  })

  handleReady = () => this.setState({ loading: false, ready: true })

  preventBadZoom = (event) => {
    if (event.detail.ratio > 1) {
      event.preventDefault();
      this.cropperRef.zoomTo(1);
    }
  }

  render() {
    const { active, pickedImage, imageHeight, imageWidth } = this.props;
    const { loading, ready, showError } = this.state;

    const cropperStyle = {
      height: FULL_VH - theme.NavbarHeight
    };

    if (!active || !pickedImage) {
      return null;
    }

    return (
      <div>
        <SmartViewNavbar>
          <NavItem left>
            <Back onClick={this.handleCancelCrop}>
              {
                isiOS() ?
                  <CancelButton>Cancel</CancelButton> :
                  <CancelIcon />
              }
            </Back>
          </NavItem>
          <NavItem center />
          <NavItem right>
            {
              ready &&
                <DoneButton onClick={this.handleCropFinished}>
                  Done
                </DoneButton>
            }
          </NavItem>
        </SmartViewNavbar>
        {loading && <Loading />}
        {showError && <Error>Problem loading image</Error>}
        <Cropper
          ref={(ref) => {
            if (ref) {
              ref.img.addEventListener('error', this.handleError);
            }
            this.cropperRef = ref;
          }}
          src={pickedImage.uri}
          style={cropperStyle}
          cropBoxMovable={false}
          cropBoxResizable={false}
          toggleDragModeOnDblclick={false}
          dragMode='move'
          aspectRatio={imageWidth / imageHeight}
          guides={false}
          autoCrop={true}
          autoCropArea={1}
          viewMode={1}
          ready={this.handleReady}
          zoom={this.preventBadZoom}
        />
      </div>
    );
  }
}

class ImagePicker extends Component {
  static _type = 'ImagePicker';
  _type = 'ImagePicker';

  static defaultProps = {
    showIcon: true,
    label: 'Pick an image',
    pickerTitle: 'Image Picker',
    pickerImageTitle: 'Current Image',
    pickerLabel: 'Pick a new image',
    imageWidth: 348,
    imageHeight: 250
  };

  state = {
    currentlyPicking: false,
    showDetails: false,
    showCropper: false,
    pickedImage: null,
    loading: false,
    uniqueId: this.props.settingsKey || Math.floor(Math.random() * Date.now())
  };

  get image() {
    const { imageUri } = this.parseValues(this.props) || {};
    return imageUri;
  }

  componentDidMount() {
    window.__webview_comms__.onNotify('image-picked', this.onImagePicked);
  }

  componentWillUnmount() {
    window.__webview_comms__.offNotify('image-picked', this.onImagePicked);
  }

  toImageUri(image) {
    return image && `data:${image.type};base64,${image.imageString}`
  }

  toImageObj(imageUri) {
    const [, type] = imageUri.match(/^data:([a-zA-Z/]+);/) || [];
    const [, imageString] = imageUri.split(',') || [];
    return {
      type,
      imageString
    };
  }

  get shouldUseFilePicker() {
    /*
     * TODO Remove optional nature of File Picker
     *
     * Three optional ways to use the web based file picker
     * Either using a global variable that can be set by the
     * environment holder (simulator for instance) or a prop
     * passed to the ImagePicker component
     */
    return USE_FILE_PICKER || // From config
      !!window.__USE_FILE_PICKER || // From window, can be set after load
      this.props._filePicker; // Comes from component props
  }

  get pickedImageSrc() {
    return this.props.showIcon && this.image;
  }

  parseValues = ({ image, imageUri } = {}) => {
    if (imageUri && imageUri.length > 0) {
      // Browser Filepicker or stored imageUri
      image = this.toImageObj(imageUri);
    } else if (image && image.type && image.imageString) {
      // Mobile Image picker or old stored image
      imageUri = this.toImageUri(image);
    } else {
      // Not a valid image picked response
      return;
    }

    return {
      image,
      imageUri
    };
  }

  onImagePicked = ({ imageSize, ...values } = {}) => {
    if (!this.shouldUseFilePicker && !this.state.currentlyPicking) {
      return;
    }

    const state = this.parseValues(values);

    if (!state) {
      // Not a valid image picked response
      return;
    }

    const { imageUri, image } = state;

    this.setState(() => {
      const { onImagePicked } = this.props;

      if (typeof onImagePicked === 'function') {
        onImagePicked({
          image,
          imageUri,
          imageSize
        });
      }

      return {
        showCropper: false,
        pickedImage: null,
        loading: false
      };
    });
  };

  onShowDetails = () => {
    this.image ?
      this.setState(() => ({
        showDetails: true
      })) :
      this.handlePickImage();
  };

  onCloseDetails = () =>
    this.setState(({ showDetails, loading }) => ({
      showDetails: false,
      showCropper: false,
      loading: false
    }));

  handlePickImage = () => {
    if (this.shouldUseFilePicker) {
      return;
    }

    const { imageWidth, imageHeight } = this.props;

    this.setState({
      currentlyPicking: true
    });

    window.__webview_comms__.launch('image-picker', {
      imageSize: {
        width: parseInt(imageWidth, 10),
        height: parseInt(imageHeight, 10)
      },
      // TODO: Remove once confirmed Android is 100% to spec
      imageSizeData: {
        width: parseInt(imageWidth, 10),
        height: parseInt(imageHeight, 10)
      }
    });
  };

  handleFilePicker = (file) => {
    const { showDetails } = this.state;

    this.setState({
      loading: true,
      showDetails: true
    }, async () => {
      this.setState({
        loading: false,
        showDetails,
        showCropper: true,
        pickedImage: {
          uri: await PromiseFileReader.readAsDataURL(file),
          mimeType: file.type
        }
      });
    });
  }

  onCropperCancel = () =>
    this.setState({ showCropper: false, pickedImage: null })

  renderCropper() {
    const { imageHeight, imageWidth } = this.props;
    const { showCropper, pickedImage } = this.state;

    return (
      <ImageCropper
        active={showCropper}
        imageHeight={imageHeight}
        imageWidth={imageWidth}
        pickedImage={pickedImage}
        onImagePicked={this.onImagePicked}
        onCancel={this.onCropperCancel}
      />
    );
  }

  renderImageDetails() {
    const { pickerImageTitle, pickerLabel, pickerTitle } = this.props;
    const { loading } = this.state;
    const image = this.image;

    return (
      <div>
        <SmartViewNavbar>
          <NavItem left>
            <Back onClick={this.onCloseDetails}>
              <BackArrow
                className="back"
                dangerouslySetInnerHTML={{__html: isiOS() ? iOSArrowBack : androidArrowBack}}
              />
              <div className="back-text">Back</div>
            </Back>
          </NavItem>
          <NavItem center>
            {pickerTitle}
          </NavItem>
          <NavItem right />
        </SmartViewNavbar>
        {
          loading ?
            <Loading /> :
            <Section title={image && pickerImageTitle}>
              {image &&
                <SectionChild>
                  <ImageDisplay
                    src={this.pickedImageSrc}
                    alt=""
                  />
                </SectionChild>
              }
              <SectionChild>
                <ImagePickerButton
                  onChange={this.handleFilePicker}
                  onClick={this.handlePickImage}
                  useFilePicker={this.shouldUseFilePicker}
                >
                  <ImagePickerRow
                    label={pickerLabel}
                  />
                  {isiOS() && <ImagePickerArrow />}
                </ImagePickerButton>
              </SectionChild>
            </Section>
        }
      </div>
    );
  }

  renderModalBody() {
    const { showDetails, showCropper, uniqueId } = this.state;

    return (
      <Modal id={'modal-image-picker-' + uniqueId} show={showDetails || showCropper}>
        {showCropper ? this.renderCropper() : this.renderImageDetails()}
      </Modal>
    );
  }

  render() {
    const {
      label,
      sublabel,
      disabled,
      title,
      description
    } = this.props;

    return (
      <Fragment>
        <SectionChild title={title} description={description}>
          <ImagePickerButton
            onChange={this.handleFilePicker}
            onClick={this.onShowDetails}
            useFilePicker={!this.image && this.shouldUseFilePicker}
            disabled={disabled}
          >
            <ImagePickerRow
              icon={this.pickedImageSrc}
              label={label}
              sublabel={sublabel}
            />
            {isiOS() && <ImagePickerArrow />}
          </ImagePickerButton>
        </SectionChild>
        {this.renderModalBody()}
      </Fragment>
    );
  }
}

export default class extends Component {
  parseValue(rawValue) {
    try {
      return JSON.parse(rawValue);
    } catch (err) {
      return;
    }
  }

  render() {
    const {
      label,
      sublabel,
      disabled,
      title,
      description,
      pickerImageTitle,
      pickerLabel,
      pickerTitle,
      imageHeight,
      imageWidth,
      image,
      imageUri,
      onImagePicked,
      settingsKey
    } = this.props;

    const pickerProps = {
      label,
      sublabel,
      disabled,
      title,
      description,
      pickerImageTitle,
      pickerLabel,
      pickerTitle,
      imageHeight,
      imageWidth,
      image,
      imageUri,
      onImagePicked
    };

    if (!settingsKey) {
      return (
        <ImagePicker {...pickerProps} />
      );
    }

    return (
      <ManagedComponent settingsKey={settingsKey}>
        {({ value, onChange }) => {
          const { image, imageUri } = this.parseValue(value) || {};
          return (
            <ImagePicker
              {...pickerProps}
              image={image}
              imageUri={imageUri}
              onImagePicked={({ image, imageUri, imageSize }) => {
                if (typeof onImagePicked === 'function') {
                  onImagePicked({ image, imageUri, imageSize });
                }
                onChange(JSON.stringify({ imageUri, imageSize }));
              }}
            />
          );
        }}
      </ManagedComponent>
    );
  }
}



// WEBPACK FOOTER //
// ./src/fitbit-components/image-picker.js